import { useState, useEffect, useMemo, useCallback } from "react";
import {
  BadgeDollarSign,
  CalendarRange,
  ChartColumnIncreasing,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleSlash,
  Download,
  History,
  Layers3,
  Search,
} from "lucide-react";
import API from "../services/api";
import DateTextInput, { formatIsoDateForInput } from "../components/DateTextInput";
import CustomSelect from "../components/CustomSelect";
import IpAuditModal from "../components/IpAuditModal";
import PremiumPairValue from "../components/PremiumPairValue";
import { getAccountSets } from "../services/sessionCache";
import { resolveAccountSetId, setStoredAccountSetId } from "../services/accountSetSelection";
import {
  formatTradeTerminalTime,
  getTradeBrokerTerminalTimeKey,
  getTradeFinalPhaseTimestampMs,
  getTradeFinalPhaseTerminalTimeKey,
} from "../utils/tradeTimes";
import "./admin/AdminShared.css";
import "./admin/AdminBroadcast.css";
import "./admin/AdminAccounts.css";
import "./admin/AdminUsers.css";
import "./ClosedTrades.css";

const ORPHAN_MARKER = "[AUTO_CLOSE:ORPHAN]";

function formatTradeTimestamp(value, fallback = "—") {
  return formatTradeTerminalTime(value, fallback);
}

function getLatestCloseDisplayKey(trade) {
  return getTradeFinalPhaseTerminalTimeKey(trade, "close");
}

function getLatestCloseTimestampMs(trade) {
  return getTradeFinalPhaseTimestampMs(trade, "close");
}

function getBrokerCloseTimeEntries(trade, selectedAccountSet) {
  const entries = [
    {
      label: selectedAccountSet?.brokers?.[0]?.brokerName || "Broker 1",
      value: getTradeBrokerTerminalTimeKey(trade, "broker1", "close"),
    },
    {
      label: selectedAccountSet?.brokers?.[1]?.brokerName || "Broker 2",
      value: getTradeBrokerTerminalTimeKey(trade, "broker2", "close"),
    },
  ].filter((entry) => Boolean(entry.value));

  if (entries.length > 0) return entries;

  const latestCloseTime = getLatestCloseDisplayKey(trade);
  if (!latestCloseTime) return [];

  return [{ label: "Final", value: latestCloseTime }];
}

function getIpAuditEntries(record, { includeManualClose = false } = {}) {
  const entries = [];

  if (record?.createIp) entries.push({ label: "Created", value: String(record.createIp) });
  if (record?.updateIp) entries.push({ label: "Updated", value: String(record.updateIp) });
  if (includeManualClose && record?.manualCloseIp) {
    entries.push({ label: "Closed", value: String(record.manualCloseIp) });
  }

  return entries;
}

export default function ClosedTrades() {
  const [accountSets, setAccountSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState("");
  const [closedTrades, setClosedTrades] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("closedAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [totalProfit, setTotalProfit] = useState(0);
  const [averageProfit, setAverageProfit] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [symbolPairs, setSymbolPairs] = useState([]);
  const [selectedPair, setSelectedPair] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalTrades, setTotalTrades] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAuditIp, setSelectedAuditIp] = useState("");

  const loadAccountSets = useCallback(async () => {
    try {
      const accountSetsData = await getAccountSets();
      if (Array.isArray(accountSetsData)) {
        setAccountSets(accountSetsData);
        if (accountSetsData.length > 0) {
          const selectedId = resolveAccountSetId(accountSetsData);
          setSelectedSetId(selectedId);
          if (selectedId) setStoredAccountSetId(selectedId);
        } else {
          setSelectedSetId("");
          setClosedTrades([]);
          setSymbolPairs([]);
          setTotalTrades(0);
          setHasMore(false);
          setTotalProfit(0);
          setAverageProfit(0);
          setLoading(false);
        }
      } else {
        setAccountSets([]);
        setSelectedSetId("");
        setClosedTrades([]);
        setSymbolPairs([]);
        setTotalTrades(0);
        setHasMore(false);
        setTotalProfit(0);
        setAverageProfit(0);
        setLoading(false);
      }
    } catch (err) {
      console.error("Failed to load account sets:", err);
      setLoading(false);
    }
  }, []);

  const loadClosedTrades = useCallback(async () => {
    if (!selectedSetId) {
      setClosedTrades([]);
      setSymbolPairs([]);
      setTotalTrades(0);
      setHasMore(false);
      setTotalProfit(0);
      setAverageProfit(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      const params = new URLSearchParams({
        accountSetId: selectedSetId,
        limit: pageSize,
        offset,
      });
      if (dateFrom) {
        params.set("from", dateFrom);
        params.set("terminalFromDate", dateFrom);
      }
      if (dateTo) {
        params.set("to", dateTo);
        params.set("terminalToDate", dateTo);
      }
      if (selectedPair) {
        const [symbol1, symbol2] = selectedPair.split("|");
        if (symbol1) params.set("symbol1", symbol1);
        if (symbol2) params.set("symbol2", symbol2);
      }
      const isSymbolsOnly = (msg, status) =>
        status === 410 || /symbols\s*-?only|collector\s*-?only/i.test(String(msg || ""));
      let res;
      try {
        res = await API.get(`/trading/closed-trades?${params.toString()}`);
        if (res.data?.success === false && isSymbolsOnly(res.data?.message, res.status)) {
          res = await API.get(`/account-sets/${selectedSetId}/closed-trades?${params.toString()}`);
        }
      } catch (err) {
        if (isSymbolsOnly(err?.response?.data?.message, err?.response?.status)) {
          res = await API.get(`/account-sets/${selectedSetId}/closed-trades?${params.toString()}`);
        } else {
          throw err;
        }
      }

      if (res.data.success) {
        const trades = res.data.trades || [];
        setClosedTrades(trades);
        setTotalTrades(res.data.count);
        setHasMore(res.data.pagination?.hasMore || false);
        setSymbolPairs(Array.isArray(res.data.symbolPairs) ? res.data.symbolPairs : []);

        const totalValue = res.data.totalProfit;
        const total =
          totalValue !== undefined && totalValue !== null
            ? parseFloat(totalValue) || 0
            : trades.reduce((sum, trade) => sum + (parseFloat(trade.totalProfit) || 0), 0);
        setTotalProfit(total);

        const countValue = Number(res.data.count);
        const tradeCount = Number.isFinite(countValue) ? countValue : trades.length;
        setAverageProfit(tradeCount > 0 ? total / tradeCount : 0);
      }
    } catch (err) {
      console.error("Failed to load closed trades:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, dateFrom, dateTo, pageSize, selectedPair, selectedSetId]);

  useEffect(() => {
    loadAccountSets();
  }, [loadAccountSets]);

  useEffect(() => {
    if (selectedSetId) {
      setCurrentPage(1);
      setSelectedPair("");
    }
  }, [selectedSetId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortDirection, sortKey, pageSize]);

  useEffect(() => {
    if (selectedSetId) {
      loadClosedTrades();
    }
  }, [selectedSetId, currentPage, dateFrom, dateTo, selectedPair, loadClosedTrades]);

  const selectedAccountSet = accountSets.find((set) => (set._id || set.id) === selectedSetId);
  const pageCount = Math.max(1, Math.ceil(totalTrades / pageSize));
  const rowStart = totalTrades === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rowEnd = totalTrades === 0 ? 0 : Math.min(currentPage * pageSize, totalTrades);

  const pairOptions = useMemo(
    () => [
      { value: "", label: "All Pairs" },
      ...symbolPairs.map((pair) => {
        const symbol1 = pair.broker1Symbol || "";
        const symbol2 = pair.broker2Symbol || "";
        const value = `${symbol1}|${symbol2}`;
        const label = symbol2 ? `${symbol1}/${symbol2}` : symbol1 || symbol2 || "Unknown";
        return { value, label };
      }),
    ],
    [symbolPairs],
  );

  const accountSetOptions = useMemo(
    () => accountSets.map((set) => ({ value: set._id || set.id, label: set.name })),
    [accountSets],
  );

  const pageSizeOptions = useMemo(
    () => [
      { value: "10", label: "10" },
      { value: "50", label: "50" },
      { value: "100", label: "100" },
    ],
    [],
  );

  const selectedPairLabel =
    pairOptions.find((option) => option.value === selectedPair)?.label || "All Pairs";

  const formatDate = (dateString) => formatTradeTimestamp(dateString);
  const dateFromLabel = formatIsoDateForInput(dateFrom) || "Start";
  const dateToLabel = formatIsoDateForInput(dateTo) || "Now";

  const isOrphanAutoClosed = (trade) => String(trade?.comment || "").includes(ORPHAN_MARKER);

  const formatCloseReasonDetail = (value) => {
    const text = String(value || "").trim();
    if (!text) return "";
    if (/^error\s*:/i.test(text)) {
      return text.replace(/^error\s*:\s*/i, "").trim();
    }
    return text;
  };

  const getCloseReasonDetail = (trade) => {
    const detail = String(trade?.closeReasonDetail || "").trim();
    const closeReason = String(trade?.closeReason || "").trim();

    if (/^manual$/i.test(detail)) {
      return trade?.manualCloseIp ? "Manual (UI/API close)" : "Manual (Generic close)";
    }

    if (detail) return formatCloseReasonDetail(detail);

    if (/manual/i.test(closeReason)) {
      return trade?.manualCloseIp ? "Manual (UI/API close)" : "Manual (Generic close)";
    }

    return formatCloseReasonDetail(closeReason) || "Unknown";
  };

  const getCloseReasonBadgeClass = (trade) => {
    if (isOrphanAutoClosed(trade)) return "admin-badge-warning";

    const closeReason = String(trade?.closeReason || "").toLowerCase();
    if (closeReason.includes("takeprofit")) return "admin-badge-success";
    if (closeReason.includes("stoploss")) return "admin-badge-danger";
    if (closeReason.includes("manual")) return "admin-badge-neutral";
    return "admin-badge-info";
  };

  const formatCloseReasonLabel = (value) => {
    const normalized = String(value || "Unknown")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .trim();
    return normalized ? normalized.replace(/\b\w/g, (char) => char.toUpperCase()) : "Unknown";
  };

  const getLatencyBadgeClass = (latency) => {
    const value = Number(latency);
    if (!Number.isFinite(value)) return "admin-badge-neutral";
    if (value <= 120) return "admin-badge-success";
    if (value <= 250) return "admin-badge-warning";
    return "admin-badge-danger";
  };

  const renderCloseReason = (trade) => {
    const detail = getCloseReasonDetail(trade);
    const closeReason = trade?.closeReason || "Unknown";
    const detailDiffers = detail && detail.toLowerCase() !== String(closeReason).toLowerCase();

    if (isOrphanAutoClosed(trade)) {
      return (
        <div className="closed-trades-reason">
          <span className="admin-badge admin-badge-warning closed-trades-reason-badge">
            Orphan Auto-Closed
          </span>
          <span className="closed-trades-reason-detail">{detail}</span>
        </div>
      );
    }

    return (
      <div className="closed-trades-reason">
        <span
          className={`admin-badge closed-trades-reason-badge ${getCloseReasonBadgeClass(trade)}`}
        >
          {formatCloseReasonLabel(closeReason)}
        </span>
        {detailDiffers && <span className="closed-trades-reason-detail">{detail}</span>}
      </div>
    );
  };

  const profitToneClass = (value) => {
    if (value > 0) return "closed-trades-value-positive";
    if (value < 0) return "closed-trades-value-negative";
    return "closed-trades-value-neutral";
  };

  const deficitToneClass = (value) =>
    value <= 0 ? "closed-trades-value-positive" : "closed-trades-value-negative";

  const handleExportCsv = useCallback(() => {
    if (closedTrades.length === 0) return;
    const headers = [
      "Ticket ID (B1)",
      "Ticket ID (B2)",
      "Direction",
      "Lot Size",
      "Broker1 Profit",
      "Broker2 Profit",
      "Opening Premium",
      "Closing Premium",
      "Deficit Premium",
      "Swap",
      "Commission",
      "B1 Latency",
      "B2 Latency",
      "Total Profit",
      "Close Reason",
      "Closed At (B1)",
      "Closed At (B2)",
      "Closed At (Final/latest)",
      "Created IP",
      "Updated IP",
      "Closed IP",
    ];
    const rows = closedTrades.map((t) => {
      const deficit = (
        (parseFloat(t.executionPremium) || 0) - (parseFloat(t.closePremium) || 0)
      ).toFixed(2);
      const latestCloseDisplayKey = getLatestCloseDisplayKey(t);
      return [
        t.broker1Ticket,
        t.broker2Ticket,
        t.broker1Direction === "Buy" ? "Buy/Sell" : "Sell/Buy",
        t.broker1Volume,
        (parseFloat(t.broker1Profit) || 0).toFixed(2),
        (parseFloat(t.broker2Profit) || 0).toFixed(2),
        (parseFloat(t.executionPremium) || 0).toFixed(2),
        (parseFloat(t.closePremium) || 0).toFixed(2),
        deficit,
        (parseFloat(t.totalSwap) || 0).toFixed(2),
        (parseFloat(t.totalCommission) || 0).toFixed(2),
        t.broker1Latency ?? "",
        t.broker2Latency ?? "",
        (parseFloat(t.totalProfit) || 0).toFixed(2),
        t.closeReason || "",
        formatTradeTimestamp(getTradeBrokerTerminalTimeKey(t, "broker1", "close"), ""),
        formatTradeTimestamp(getTradeBrokerTerminalTimeKey(t, "broker2", "close"), ""),
        latestCloseDisplayKey === null ? "" : formatTradeTimestamp(latestCloseDisplayKey, ""),
        t.createIp || "-",
        t.updateIp || "-",
        t.manualCloseIp || "-",
      ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`);
    });
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `closed-trades-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [closedTrades]);

  const skeletonRows = useMemo(() => Array.from({ length: 6 }), []);

  const processedTrades = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const filtered = closedTrades.filter((trade) => {
      if (!term) return true;
      return [
        trade.tradeId,
        trade.broker1Ticket,
        trade.broker2Ticket,
        trade.broker1Direction,
        trade.closeReason,
        trade.closeReasonDetail,
        trade.comment,
      ]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(term));
    });

    const toNumber = (value) => parseFloat(value) || 0;

    return [...filtered].sort((left, right) => {
      const getComparable = (trade) => {
        switch (sortKey) {
          case "ticket1":
            return String(trade.broker1Ticket || "");
          case "ticket2":
            return String(trade.broker2Ticket || "");
          case "direction":
            return String(trade.broker1Direction || "");
          case "lots":
            return toNumber(trade.broker1Volume);
          case "broker1Profit":
            return toNumber(trade.broker1Profit);
          case "broker2Profit":
            return toNumber(trade.broker2Profit);
          case "openingPremium":
            return toNumber(trade.executionPremium);
          case "closingPremium":
            return toNumber(trade.closePremium);
          case "deficitPremium":
            return toNumber(trade.executionPremium) - toNumber(trade.closePremium);
          case "swap":
            return toNumber(trade.totalSwap);
          case "commission":
            return toNumber(trade.totalCommission);
          case "totalProfit":
            return toNumber(trade.totalProfit);
          case "closedAt":
          default:
            return getLatestCloseTimestampMs(trade) ?? 0;
        }
      };

      const a = getComparable(left);
      const b = getComparable(right);
      if (a < b) return sortDirection === "asc" ? -1 : 1;
      if (a > b) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [closedTrades, searchTerm, sortDirection, sortKey]);

  const handleSort = (key) => {
    setSortDirection((currentDirection) => {
      if (sortKey === key) {
        return currentDirection === "asc" ? "desc" : "asc";
      }
      return "desc";
    });
    setSortKey(key);
  };

  const renderHeaderSort = (label, key) => {
    const active = sortKey === key;
    return (
      <button
        type="button"
        className={`closed-trades-sort${active ? " closed-trades-sort-active" : ""}`}
        onClick={() => handleSort(key)}
      >
        <span>{label}</span>
        {active ? (
          <ChevronDown
            className={`closed-trades-sort-icon${sortDirection === "desc" ? " closed-trades-sort-icon-desc" : ""}`}
          />
        ) : (
          <ChevronsUpDown className="closed-trades-sort-icon" />
        )}
      </button>
    );
  };

  return (
    <div className="admin-page admin-broadcast-page closed-trades-page">
      <div className="closed-trades-shell">
        <div className="admin-broadcast-header">
          <div>
            <h1>
              <History className="admin-broadcast-header-icon" />
              <span>Closed Trades</span>
            </h1>
            <p>
              Review completed positions, close reasons, and performance with the same admin table
              styling.
            </p>
          </div>
        </div>

        <div className="admin-broadcast-summary-grid closed-trades-summary-grid">
          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <Layers3 size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Account Set</span>
              <strong>{selectedAccountSet?.name || "No Set"}</strong>
              <small>{selectedPairLabel}</small>
            </div>
          </div>
          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <ChartColumnIncreasing size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Total Closed</span>
              <strong>{totalTrades}</strong>
              <small>
                {rowStart}-{rowEnd} visible on this page
              </small>
            </div>
          </div>
          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <BadgeDollarSign size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Total Profit</span>
              <strong className={profitToneClass(totalProfit)}>${totalProfit.toFixed(2)}</strong>
              <small>Average ${averageProfit.toFixed(2)} per trade</small>
            </div>
          </div>
          <div className="admin-broadcast-summary-card">
            <div className="admin-broadcast-summary-icon">
              <CalendarRange size={20} />
            </div>
            <div className="admin-broadcast-summary-copy">
              <span>Date Range</span>
              <strong>{dateFrom || dateTo ? "Filtered" : "All Time"}</strong>
              <small>
                {dateFromLabel} to {dateToLabel}
              </small>
            </div>
          </div>
        </div>

        <section className="admin-card admin-broadcast-card">
          <div className="admin-card-heading">
            <div className="admin-broadcast-card-copy">
              <div className="admin-card-title">
                <History />
                <span>Closed Trades History</span>
              </div>
              <p>Review completed trades, track close reasons, and analyze premium performance.</p>
            </div>
          </div>

          <div className="closed-trades-toolbar closed-trades-toolbar-primary">
            <div className="admin-field-group admin-users-toolbar-search">
              <label className="admin-label">
                <Search size={13} />
                Search
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="Search by ticket, reason, or direction"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="admin-field-group closed-trades-count-field">
              <label className="admin-label">Count</label>
              <CustomSelect
                value={String(pageSize)}
                onChange={(value) => setPageSize(Number(value))}
                options={pageSizeOptions}
              />
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-ghost closed-trades-clear-btn"
              onClick={handleExportCsv}
              disabled={loading || closedTrades.length === 0}
              aria-label="Export CSV"
              title="Export CSV"
            >
              <Download size={15} />
              <span className="closed-trades-clear-label">Export</span>
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-ghost closed-trades-clear-btn"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setSelectedPair("");
                setSearchTerm("");
                setCurrentPage(1);
              }}
              aria-label="Clear filters"
              title="Clear filters"
            >
              <CircleSlash size={15} />
              <span className="closed-trades-clear-label">Clear</span>
            </button>
          </div>

          <div className="closed-trades-toolbar closed-trades-toolbar-secondary">
            <div className="admin-field-group">
              <label className="admin-label">Account Set</label>
              <CustomSelect
                value={selectedSetId}
                onChange={(value) => {
                  setSelectedSetId(value);
                  setStoredAccountSetId(value);
                }}
                options={accountSetOptions}
                placeholder="Select account set"
              />
            </div>
            <div className="admin-field-group">
              <label className="admin-label">Symbol Pair</label>
              <CustomSelect
                value={selectedPair}
                onChange={(value) => {
                  setSelectedPair(value);
                  setCurrentPage(1);
                }}
                options={pairOptions}
              />
            </div>
            <div className="admin-field-group">
              <DateTextInput
                label="From"
                value={dateFrom}
                maxValue={dateTo}
                onChange={(value) => {
                  setDateFrom(value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="admin-field-group">
              <DateTextInput
                label="To"
                value={dateTo}
                minValue={dateFrom}
                onChange={(value) => {
                  setDateTo(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {selectedAccountSet && (
            <div className="admin-meta closed-trades-account-meta">
              <span>
                Broker 1: {selectedAccountSet.brokers[0]?.terminal} (
                {selectedAccountSet.brokers[0]?.accountNumber}-
                {selectedAccountSet.brokers[0]?.brokerName})
              </span>
              <span>
                Broker 2: {selectedAccountSet.brokers[1]?.terminal} (
                {selectedAccountSet.brokers[1]?.accountNumber}-
                {selectedAccountSet.brokers[1]?.brokerName})
              </span>
            </div>
          )}

          <div className="admin-data-table-wrap admin-users-table-wrap closed-trades-table-wrap">
            <table className="admin-data-table admin-users-table closed-trades-table">
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>IP Audit</th>
                  <th>{renderHeaderSort("Ticket ID (Broker 1)", "ticket1")}</th>
                  <th>{renderHeaderSort("Ticket ID (Broker 2)", "ticket2")}</th>
                  <th>{renderHeaderSort("Direction", "direction")}</th>
                  <th>{renderHeaderSort("Lot Size", "lots")}</th>
                  <th>{renderHeaderSort("Broker1 Profit", "broker1Profit")}</th>
                  <th>{renderHeaderSort("Broker2 Profit", "broker2Profit")}</th>
                  <th>{renderHeaderSort("Opening Premium", "openingPremium")}</th>
                  <th>{renderHeaderSort("Closing Premium", "closingPremium")}</th>
                  <th>{renderHeaderSort("Deficit Premium", "deficitPremium")}</th>
                  <th>{renderHeaderSort("Swap", "swap")}</th>
                  <th>{renderHeaderSort("Commission", "commission")}</th>
                  <th>Latency</th>
                  <th>{renderHeaderSort("Total Profit", "totalProfit")}</th>
                  <th>Close Reason</th>
                  <th>{renderHeaderSort("Closed At", "closedAt")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  skeletonRows.map((_, index) => (
                    <tr
                      key={`closed-trades-skeleton-${index}`}
                      className="admin-table-skeleton-row"
                    >
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-sm" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-lg" />
                      </td>
                      <td>
                        <span className="admin-skeleton-line admin-skeleton-line-md" />
                      </td>
                    </tr>
                  ))
                ) : closedTrades.length === 0 ? (
                  <tr className="admin-accounts-empty-table-row">
                    <td colSpan={17} className="admin-accounts-empty-table-cell">
                      <div className="admin-accounts-empty-table-state">
                        {accountSets.length === 0
                          ? "No linked account sets found."
                          : "No closed trades found."}
                      </div>
                    </td>
                  </tr>
                ) : processedTrades.length === 0 ? (
                  <tr className="admin-accounts-empty-table-row">
                    <td colSpan={17} className="admin-accounts-empty-table-cell">
                      <div className="admin-accounts-empty-table-state">
                        No closed trades match the current filters.
                      </div>
                    </td>
                  </tr>
                ) : (
                  processedTrades.map((trade, index) => {
                    const totalValue = parseFloat(trade.totalProfit) || 0;
                    const broker1Value = parseFloat(trade.broker1Profit) || 0;
                    const broker2Value = parseFloat(trade.broker2Profit) || 0;
                    const deficitValue =
                      (parseFloat(trade.executionPremium) || 0) -
                      (parseFloat(trade.closePremium) || 0);
                    const ipAuditEntries = getIpAuditEntries(trade, { includeManualClose: true });
                    const closeTimeEntries = getBrokerCloseTimeEntries(trade, selectedAccountSet);

                    return (
                      <tr
                        key={
                          trade.tradeId || `${trade.broker1Ticket}-${trade.broker2Ticket}-${index}`
                        }
                      >
                        <td>{rowStart + index}</td>
                        <td className="closed-trades-ip-audit-cell">
                          {ipAuditEntries.length ? (
                            <div className="admin-ip-audit">
                              {ipAuditEntries.map((entry) => (
                                <button
                                  key={`${entry.label}-${entry.value}`}
                                  type="button"
                                  className="admin-ip-audit-trigger"
                                  onClick={() => setSelectedAuditIp(entry.value)}
                                  title={`Inspect ${entry.value}`}
                                >
                                  <span className="admin-ip-audit-label">{entry.label}</span>
                                  <span className="admin-ip-audit-value">{entry.value}</span>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className="admin-ip-audit-empty">—</span>
                          )}
                        </td>
                        <td className="closed-trades-strong">{trade.broker1Ticket || "—"}</td>
                        <td className="closed-trades-strong">{trade.broker2Ticket || "—"}</td>
                        <td>
                          <span
                            className={`admin-badge ${trade.broker1Direction === "Buy" ? "admin-badge-danger" : "admin-badge-success"}`}
                          >
                            {trade.broker1Direction === "Buy" ? "Buy/Sell" : "Sell/Buy"}
                          </span>
                        </td>
                        <td>{trade.broker1Volume || "—"}</td>
                        <td className={`closed-trades-strong ${profitToneClass(broker1Value)}`}>
                          ${broker1Value.toFixed(2)}
                        </td>
                        <td className={`closed-trades-strong ${profitToneClass(broker2Value)}`}>
                          ${broker2Value.toFixed(2)}
                        </td>
                        <td className="closed-trades-strong">
                          <PremiumPairValue
                            direction={trade.broker1Direction}
                            phase="entry"
                            primaryPremium={trade.executionPremium}
                            showEmptyRows={false}
                          />
                        </td>
                        <td className="closed-trades-strong">
                          <PremiumPairValue
                            direction={trade.broker1Direction}
                            phase="exit"
                            primaryPremium={trade.closePremium}
                            showEmptyRows={false}
                          />
                        </td>
                        <td className={`closed-trades-strong ${deficitToneClass(deficitValue)}`}>
                          {deficitValue.toFixed(2)}
                        </td>
                        <td>${(parseFloat(trade.totalSwap) || 0).toFixed(2)}</td>
                        <td>${(parseFloat(trade.totalCommission) || 0).toFixed(2)}</td>
                        <td>
                          <div className="closed-trades-latency">
                            <span
                              className={`admin-badge ${getLatencyBadgeClass(trade.broker1Latency)}`}
                            >
                              B1{" "}
                              {Number.isFinite(Number(trade.broker1Latency))
                                ? `${trade.broker1Latency}ms`
                                : "—"}
                            </span>
                            <span
                              className={`admin-badge ${getLatencyBadgeClass(trade.broker2Latency)}`}
                            >
                              B2{" "}
                              {Number.isFinite(Number(trade.broker2Latency))
                                ? `${trade.broker2Latency}ms`
                                : "—"}
                            </span>
                          </div>
                        </td>
                        <td className={`closed-trades-total-profit ${profitToneClass(totalValue)}`}>
                          ${totalValue.toFixed(2)}
                        </td>
                        <td>{renderCloseReason(trade)}</td>
                        <td>
                          {closeTimeEntries.length > 0 ? (
                            <div className="closed-trades-close-times">
                              {closeTimeEntries.map((entry) => (
                                <div key={entry.label} className="closed-trades-close-time-row">
                                  <span className="closed-trades-close-time-label">
                                    {entry.label}
                                  </span>
                                  <span className="closed-trades-close-time-value">
                                    ({formatDate(entry.value)})
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!loading && processedTrades.length > 0 && (
            <div className="admin-table-footer admin-users-table-footer closed-trades-table-footer">
              <span className="admin-table-summary">
                Showing {rowStart}-{Math.min(rowStart + processedTrades.length - 1, rowEnd)} of{" "}
                {totalTrades}
              </span>
              <div className="admin-table-pagination">
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={15} />
                  Prev
                </button>
                <span className="admin-users-pagecount closed-trades-pagecount">
                  Page {currentPage} / {pageCount}
                </span>
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={!hasMore}
                >
                  Next
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
      <IpAuditModal
        open={Boolean(selectedAuditIp)}
        ip={selectedAuditIp}
        onClose={() => setSelectedAuditIp("")}
      />
    </div>
  );
}
